// ============================================================
// WINNERSAURUS — Error Boundary
//
// Catches any unexpected React render errors and shows a
// clean fallback instead of a blank/broken page.
// Wrap around any component that makes API calls.
// ============================================================

import React from 'react';

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Log to console in development — swap for Sentry/LogRocket later
    console.error('Winnersaurus ErrorBoundary caught:', error, info);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
    if (this.props.onReset) this.props.onReset();
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <div style={{
        minHeight: this.props.fullPage ? '100vh' : '400px',
        background: 'var(--bg)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px 5%',
      }}>
        <div style={{ textAlign: 'center', maxWidth: '480px' }}>

          {/* Icon */}
          <div style={{
            width: '56px', height: '56px', borderRadius: '50%',
            background: 'rgba(245,66,90,0.1)', border: '1px solid rgba(245,66,90,0.25)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 24px',
          }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#f5425a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/>
              <line x1="12" y1="8" x2="12" y2="12"/>
              <line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
          </div>

          <p style={{
            fontFamily: 'var(--font-mono)', fontSize: '11px',
            color: '#f5425a', letterSpacing: '2.5px',
            textTransform: 'uppercase', marginBottom: '12px',
          }}>
            Something went wrong
          </p>

          <h2 style={{
            fontFamily: 'var(--font-display)', fontSize: '28px',
            fontWeight: 800, color: 'var(--text)', textTransform: 'uppercase',
            letterSpacing: '-0.5px', marginBottom: '16px',
          }}>
            Unexpected Error
          </h2>

          <p style={{
            fontSize: '15px', color: 'var(--text-muted)',
            lineHeight: 1.7, marginBottom: '32px',
          }}>
            Something broke unexpectedly. This has been logged. Try refreshing the page — if the problem persists, contact us at hello@winnersaurus.com.
          </p>

          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={() => window.location.reload()}
              style={{
                background: 'var(--green)', color: '#060d07', border: 'none',
                padding: '12px 28px', borderRadius: 'var(--radius)',
                fontFamily: 'var(--font-body)', fontWeight: 700,
                fontSize: '14px', cursor: 'pointer', transition: 'opacity 0.2s',
              }}
              onMouseEnter={e => e.currentTarget.style.opacity = '0.9'}
              onMouseLeave={e => e.currentTarget.style.opacity = '1'}
            >
              Refresh Page
            </button>
            <button
              onClick={this.handleReset}
              style={{
                background: 'transparent', color: 'var(--text-muted)',
                border: '1px solid var(--border)', padding: '12px 28px',
                borderRadius: 'var(--radius)', fontFamily: 'var(--font-body)',
                fontWeight: 500, fontSize: '14px', cursor: 'pointer',
                transition: 'border-color 0.2s',
              }}
              onMouseEnter={e => e.currentTarget.style.borderColor = 'rgba(237,242,238,0.4)'}
              onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}
            >
              Try Again
            </button>
          </div>

        </div>
      </div>
    );
  }
}
